import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getBlogPosts } from '../../services/CommonServices';
import { Link } from 'react-router-dom';
import Constants from 'constants/Constants';
import LoginStore from 'stores/LoginStore';
import EditableObjectHandle from '../Common/EditableObjectHandler';
import IntlObject from '../Common/IntlObject';

const Section = styled.section`
  position: relative;
  padding: 60px 0;
  background-color: ${props => props.backgroundColor || '#f9f9f9'};
  width: 100%;

  @media (max-width: 1400px) {
    padding: 40px 20px;
  }
`;

const Container = styled.div`
  @media (min-width: 1401px) {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 15px;
  }

  @media (max-width: 1400px) {
    max-width: 100%;
    padding: 0;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 30px;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  letter-spacing: 2px;
`;

const TabContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const TabButton = styled.button`
  background: transparent;
  border: none;
  padding: 12px 16px;
  font-size: 0.85rem;
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? '#333' : '#888'};
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  text-align: left;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 100%;
    height: 3px;
    background-color: ${props => props.active ? '#333' : 'transparent'};
    transition: background-color 0.3s ease;
  }

  &:hover {
    color: #333;
  }

  @media (max-width: 768px) {
    flex-shrink: 0;
    min-width: 150px;
    padding: 10px 14px;
    font-size: 0.8rem;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
`;

const ContentArea = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  font-size: 0.82rem;
  line-height: 1.7;
  color: #333;

  /* CSS Multi-column 레이아웃 */
  column-count: 3;
  column-gap: 30px;
  column-rule: 1px solid #e0e0e0;
  column-fill: balance;

  @media (max-width: 992px) {
    column-count: 2;
    column-gap: 24px;
  }

  @media (max-width: 768px) {
    column-count: 1;
    padding: 20px;
    max-height: ${props => props.isMobile ? '400px' : 'none'};
    overflow: hidden;
  }

  /* 헤딩 스타일 */
  h1, h2, h3, h4, h5, h6 {
    margin-top: 0;
    margin-bottom: 0.5em;
    font-weight: 600;
    line-height: 1.3;
    color: #1a1a1a;
    break-after: avoid;
  }

  h1 { font-size: 1.1rem; }
  h2 { font-size: 1rem; }
  h3 { font-size: 0.95rem; }
  h4 { font-size: 0.9rem; }

  /* 문단 스타일 */
  p {
    margin: 0 0 0.8em 0;
    text-align: justify;
    orphans: 3;
    widows: 3;
  }

  /* 리스트 스타일 */
  ul, ol {
    margin: 0 0 0.8em 0;
    padding-left: 1.2em;
  }

  li {
    margin-bottom: 0.3em;
  }

  /* 인용구 스타일 */
  blockquote {
    border-left: 3px solid #c41e3a;
    padding-left: 12px;
    margin: 0.8em 0;
    color: #555;
    font-style: italic;
    font-size: 0.78rem;
    break-inside: avoid;
  }

  /* 이미지 스타일 */
  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 0.5em 0;
    break-inside: avoid;
  }

  /* 링크 스타일 */
  a {
    color: #c41e3a;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const GradientOverlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.8) 50%,
      rgba(255, 255, 255, 1) 100%
    );
    pointer-events: none;
    border-radius: 0 0 8px 8px;
  }
`;

const ViewFullButton = styled(Link)`
  display: none;

  @media (max-width: 768px) {
    display: block;
    text-align: center;
    margin-top: 20px;
    padding: 10px 20px;
    background-color: #333;
    color: #fff;
    border-radius: 6px;
    text-decoration: none;
    font-weight: 500;
    font-size: 0.8rem;
    transition: background-color 0.3s ease;

    &:hover {
      background-color: #555;
      color: #fff;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 0.85rem;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 0.85rem;
`;

const BlogTabSection = ({ siteId, id, options = {} }) => {
  const [posts, setPosts] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [authInfo, setAuthInfo] = useState(LoginStore.authInfo);

  // Default objects for editing
  const defaultObjects = {
    fullWidth: 'true',
    backgroundColor: '#f9f9f9',
    selectedPosts: [],
  };

  // Options with defaults
  const backgroundColor = options.backgroundColor || '#f9f9f9';

  // Auth listener
  useEffect(() => {
    const onAuthChange = () => setAuthInfo(LoginStore.authInfo);
    LoginStore.addChangeListener(onAuthChange);
    return () => LoginStore.removeChangeListener(onAuthChange);
  }, []);

  const isLogin = authInfo ? authInfo.status === Constants.AUTHED : false;

  // Fetch posts
  useEffect(() => {
    if (!siteId) {
      setLoading(false);
      return;
    }

    const selectedPostIds = options.selectedPosts || [];

    setLoading(true);
    getBlogPosts(siteId, { limit: 20 })
      .then(res => {
        if (res.data.success) {
          const allPosts = res.data.data.posts.filter(p => p.is_published);

          // If specific posts are selected, filter and order by selection
          if (selectedPostIds && selectedPostIds.length > 0) {
            const selectedPosts = selectedPostIds
              .map(postId => allPosts.find(p => p.id === postId || p.id === parseInt(postId)))
              .filter(Boolean)
              .slice(0, 3);
            setPosts(selectedPosts);
          } else {
            // Default: show latest 3 posts
            setPosts(allPosts.slice(0, 3));
          }
        }
      })
      .catch(err => {
        console.error('Error fetching blog posts:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [siteId, options.selectedPosts]);

  // Reset active tab when posts change
  useEffect(() => {
    setActiveTab(0);
  }, [posts]);

  if (loading) {
    return (
      <Section backgroundColor={backgroundColor}>
        <EditableObjectHandle
          id={id}
          objects={options || defaultObjects}
          defaultObjects={defaultObjects}
          isAdmin={isLogin}
          componentTitle="BlogTabSection"
        />
        <Container>
          <SectionTitle>
            <IntlObject keycode="blogtab.title" returnType="span" />
          </SectionTitle>
          <LoadingState>
            <IntlObject keycode="blog.loading" returnType="span" />
          </LoadingState>
        </Container>
      </Section>
    );
  }

  if (posts.length === 0) {
    return (
      <Section backgroundColor={backgroundColor}>
        <EditableObjectHandle
          id={id}
          objects={options || defaultObjects}
          defaultObjects={defaultObjects}
          isAdmin={isLogin}
          componentTitle="BlogTabSection"
        />
        <Container>
          <SectionTitle>
            <IntlObject keycode="blogtab.title" returnType="span" />
          </SectionTitle>
          <EmptyState>
            <IntlObject keycode="blog.no_posts" returnType="span" />
          </EmptyState>
        </Container>
      </Section>
    );
  }

  const currentPost = posts[activeTab];

  return (
    <Section backgroundColor={backgroundColor}>
      <EditableObjectHandle
        id={id}
        objects={options || defaultObjects}
        defaultObjects={defaultObjects}
        isAdmin={isLogin}
        componentTitle="BlogTabSection"
      />
      <Container>
        <SectionTitle>
          <IntlObject keycode="blogtab.title" returnType="span" />
        </SectionTitle>

        <TabContainer>
          {posts.map((post, index) => (
            <TabButton
              key={post.id}
              active={activeTab === index}
              onClick={() => setActiveTab(index)}
              role="tab"
              aria-selected={activeTab === index}
            >
              {post.title}
            </TabButton>
          ))}
        </TabContainer>

        <ContentWrapper>
          <ContentArea
            isMobile={true}
            dangerouslySetInnerHTML={{ __html: currentPost.content }}
          />
          <GradientOverlay />
        </ContentWrapper>

        <ViewFullButton to={`/blog/${currentPost.slug}`}>
          <IntlObject keycode="blog.view_full" returnType="span" /> &rarr;
        </ViewFullButton>
      </Container>
    </Section>
  );
};

export default BlogTabSection;
